import { XOP_CONTACT } from "../../lib/company.js";

/**
 * The legal pages' text (2026-09-14): the Terms of Use and the Privacy
 * Policy for the XOP Reseller portal, as plain data so the two pages are
 * one component and the words can be pinned by a test.
 *
 * Written as standard terms for a business portal. They say what the portal
 * does and does not do, in the company's name; the Reseller Agreement and
 * NDA a company signs when it applies remain the contract that governs the
 * commercial relationship, and both pages say so.
 */
export interface LegalSection {
  id: string;
  title: string;
  paragraphs: readonly string[];
  bullets?: readonly string[];
}

export interface LegalPageContent {
  title: string;
  lede: string;
  /** ISO date; printed as "Last updated …". */
  updated: string;
  sections: readonly LegalSection[];
}

const COMPANY = XOP_CONTACT.company;

export const TERMS_OF_USE: LegalPageContent = {
  title: "Terms of Use",
  lede: `These terms govern your use of XOP Reseller, the partner portal ${COMPANY} provides to its authorised resellers and channel partners. By signing in you agree to them on behalf of the company you represent.`,
  updated: "2026-09-14",
  sections: [
    {
      id: "who",
      title: "1. Who may use the portal",
      paragraphs: [
        `The portal is for companies that ${COMPANY} has accepted into its Reseller Program and for the people those companies invite. An account is personal: it is issued to a named person at a reseller company, it may not be shared, and the company is responsible for the people it invites and for removing their access when they leave.`,
        "Access may be limited by role. Your company's owner decides what each person may see and do inside the portal; XOP Networks decides what the company as a whole may see.",
      ],
    },
    {
      id: "agreement",
      title: "2. The Reseller Agreement comes first",
      paragraphs: [
        `Your commercial relationship with ${COMPANY} — pricing, territory, payment terms, confidentiality, support — is governed by the Reseller Agreement and Non-Disclosure Agreement your company signed when it joined the program. These Terms of Use cover the portal only. If they conflict with the signed agreements, the signed agreements prevail.`,
      ],
    },
    {
      id: "what",
      title: "3. What the portal is for",
      paragraphs: [
        "The portal lets your company manage the customers and prospects it serves, ask XOP Networks for quotes and see the invoices XOP Networks issues to it, build and download licences for the appliances it installs, and read the documentation and download the software XOP Networks publishes for its partners.",
      ],
      bullets: [
        "A quote is a request: XOP Networks prices it and issues it, and it is valid for the period stated on it.",
        "An invoice in the portal is XOP Networks' invoice to your company. It is payable as stated on the invoice, under the payment terms in the Reseller Agreement.",
        "A licence file is issued for one identified appliance and for the term stated on it. A term longer than a trial is subject to XOP Networks' approval.",
        "Software images, release notes and documentation are provided for installing and supporting XOP Networks products for your customers, and for nothing else.",
      ],
    },
    {
      id: "conduct",
      title: "4. What you agree not to do",
      paragraphs: ["Using the portal, you and the people at your company agree not to:"],
      bullets: [
        "share sign-in details, or let anyone use an account that was not issued to them;",
        "copy, redistribute or resell software, licence files, documentation or pricing obtained through the portal except as the Reseller Agreement allows;",
        "attempt to access another company's data, probe or overload the service, or bypass any access control;",
        "enter information you know to be false, or information about a customer that you are not entitled to hold.",
      ],
    },
    {
      id: "records",
      title: "5. Records and downloads are logged",
      paragraphs: [
        `Every download of a software image or licence file, and every quote decision, is recorded against the person who made it, with the time and the network address it came from. ${COMPANY} keeps these records to meet its own licensing and export obligations and to resolve disputes. Refused attempts are recorded as well.`,
      ],
    },
    {
      id: "availability",
      title: "6. Availability and changes",
      paragraphs: [
        `${COMPANY} aims to keep the portal available at all times but does not guarantee it. It may change, suspend or withdraw any part of the portal, and may update these terms; a material change will be announced in the portal or by email to your company's owner. Continuing to use the portal after a change is acceptance of it.`,
      ],
    },
    {
      id: "liability",
      title: "7. Liability",
      paragraphs: [
        `The portal is provided as is. To the extent permitted by law, ${COMPANY} is not liable for loss arising from the portal being unavailable, from information in it being incomplete or out of date, or from your company's own use of what it obtains through the portal. Nothing here limits liability that cannot be limited by law, and nothing here reduces what the Reseller Agreement provides.`,
      ],
    },
    {
      id: "termination",
      title: "8. Ending access",
      paragraphs: [
        `Your company's owner may remove any person's access at any time. ${COMPANY} may suspend or close a person's or a company's access for breach of these terms or of the Reseller Agreement, or when the Reseller Agreement ends. Records kept under section 5 survive the closing of an account.`,
      ],
    },
    {
      id: "contact",
      title: "9. Contact",
      paragraphs: [
        `Questions about these terms go to ${COMPANY}, ${XOP_CONTACT.addressLine}, ${XOP_CONTACT.sales}, ${XOP_CONTACT.phone}.`,
      ],
    },
  ],
};

export const PRIVACY_POLICY: LegalPageContent = {
  title: "Privacy Policy",
  lede: `This policy explains what ${COMPANY} collects through the XOP Reseller portal, why, who can see it and how long it is kept. It applies to the people who sign in to the portal and to the customer and contact information their companies enter into it.`,
  updated: "2026-09-14",
  sections: [
    {
      id: "collect",
      title: "1. What we collect",
      paragraphs: ["Through the portal we hold three kinds of information:"],
      bullets: [
        "Account information about you: your name, work email address, role at your company, and the times you sign in.",
        "Business information your company enters: its customers and prospects, their contact people, the appliances it licenses, the quotes it requests and the invoices it is issued.",
        "Activity records: which documents, software images and licence files were downloaded, by whom, when and from which network address; and which quotes were accepted or declined.",
      ],
    },
    {
      id: "why",
      title: "2. Why we collect it",
      paragraphs: [
        "We use this information to run the Reseller Program: to give the right people at your company access to the right things, to price and issue quotes and invoices, to issue licence files for the appliances you install, to support the products you resell, and to keep the records our licensing and export obligations require.",
        "We do not sell this information, and we do not use it for advertising.",
      ],
    },
    {
      id: "who",
      title: "3. Who can see it",
      paragraphs: [
        `Inside ${COMPANY}, the staff who manage the Reseller Program, sales, support and finance can see your company's information as their work requires. Inside your company, what a person can see is decided by the role and permissions your company's owner gives them; nobody at another reseller company can see your company's information.`,
        "Service providers that host and operate the portal for us process the information on our instructions and under confidentiality obligations. We disclose information to authorities where the law requires it.",
      ],
    },
    {
      id: "customers",
      title: "4. Your customers' information",
      paragraphs: [
        `The customer and contact information your company enters is information your company is responsible for. You confirm that you are entitled to hold it and to share it with ${COMPANY} for the purposes above, and that you will keep it accurate. We hold it on your company's behalf and use it only to serve those customers with you.`,
      ],
    },
    {
      id: "retention",
      title: "5. How long we keep it",
      paragraphs: [
        "Account information is kept while a person has access and for a reasonable period afterwards. Business information is kept while your company is in the Reseller Program and for as long as our contractual, tax and licensing obligations require after it leaves. Download and decision records are kept for the life of the licences and releases they relate to, because they are the evidence of who was entitled to what.",
      ],
    },
    {
      id: "security",
      title: "6. How we protect it",
      paragraphs: [
        "The portal is served over encrypted connections, sign-in requires a second factor, every request is checked against the signed-in person's company and permissions, and software and licence downloads are granted one at a time through short-lived addresses. We keep the systems behind the portal patched and monitored.",
      ],
    },
    {
      id: "cookies",
      title: "7. Cookies and browser storage",
      paragraphs: [
        "The portal uses a session cookie to keep you signed in and a small amount of browser storage for your own conveniences, such as your theme choice and which notifications you have already read. It uses no advertising or third-party tracking cookies.",
      ],
    },
    {
      id: "rights",
      title: "8. Your rights",
      paragraphs: [
        `You may ask to see the information we hold about you, to have it corrected, or to have your account closed. Your company's owner can update most of it directly in the portal. For anything else, contact ${COMPANY} at ${XOP_CONTACT.support}. Where the law gives you further rights over your information, this policy does not limit them.`,
      ],
    },
    {
      id: "changes",
      title: "9. Changes to this policy",
      paragraphs: [
        "We will update this page when our practices change and note the date at the top. A material change will be announced in the portal or by email to your company's owner.",
      ],
    },
    {
      id: "contact",
      title: "10. Contact",
      paragraphs: [
        `Privacy questions go to ${COMPANY}, ${XOP_CONTACT.addressLine}, ${XOP_CONTACT.support}, ${XOP_CONTACT.phone}.`,
      ],
    },
  ],
};
